import React, { useState, useEffect, useMemo, useRef } from 'react';
import { User, CreateUserData, Company, CreateCompanyData, EskimiAdvertiser, PaginationInfo } from '../types';
import { userApi, companyApi, advertisersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/ui/pagination';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs } from '../components/ui/tabs';
import { Modal } from '../components/ui/modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/table';
import { TableFillBox, fillOrHugClass, tableFillHeightClass } from '../components/ui/tableFillBox';
import { ActionIconButton } from '../components/ui/ActionIconButton';
import ActionTooltip from '../components/promptManager/ActionTooltip';
import { PencilIcon, TrashIcon, KeyIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// App-standard pill input/select — fully rounded to match SearchInput's
// pill chrome, per the locked Manage Users modal spec.
const PILL_INPUT = 'w-full rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';

// Helper function to format role display
const formatRoleDisplay = (role: string): string => {
  switch (role) {
    case 'user':
      return 'User';
    case 'eskimi_admin':
      return 'Eskimi User';
    case 'adops':
      return 'AdOps';
    case 'admin':
      return 'Admin';
    default:
      return role;
  }
};

// Role badge mapping per the locked spec: Admin=nav-active (default), Eskimi
// Admin/AdOps=warn, User=ghost (secondary).
const roleBadgeVariant = (role: string): 'default' | 'warn' | 'secondary' => {
  if (role === 'admin') return 'default';
  if (role === 'eskimi_admin' || role === 'adops') return 'warn';
  return 'secondary';
};

// First + last initials for the brand-gradient avatar chip (falls back to a
// single initial for one-word names).
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    name: '',
    role: 'eskimi_admin',
    provider: 'local',
    companyId: ''
  });

  // Company management state
  const [activeTab, setActiveTab] = useState<'users' | 'companies'>('users');
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyFormData, setCompanyFormData] = useState<CreateCompanyData>({
    name: '',
    advertiserId: null
  });
  const [advertisers, setAdvertisers] = useState<EskimiAdvertiser[]>([]);
  const [advertiserSearchTerm, setAdvertiserSearchTerm] = useState('');
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);
  const [companyAdvertisers, setCompanyAdvertisers] = useState<{[key: number]: EskimiAdvertiser}>({});
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Advertiser IDs matching the current companySearchTerm, resolved via the
  // advertisers search endpoint so name search covers the whole dataset —
  // not just whichever advertisers happen to be cached from paginating.
  const [companySearchAdvertiserIds, setCompanySearchAdvertiserIds] = useState<Set<number>>(new Set());

  // Search and pagination state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [companyPage, setCompanyPage] = useState(1);
  const itemsPerPage = 10;

  // Registration link state
  const [showRegistrationLinkModal, setShowRegistrationLinkModal] = useState(false);
  const [registrationLink, setRegistrationLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getAllUsers();
      setUsers(response);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyApi.getAll();
      setCompanies(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch companies');
    }
  };

  const searchAdvertisers = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setAdvertisers([]);
      return;
    }

    setLoadingAdvertisers(true);
    try {
      const response = await advertisersApi.search(searchTerm);
      setAdvertisers(response);
    } catch (err: any) {
      console.error('Failed to search advertisers:', err);
      setAdvertisers([]);
    } finally {
      setLoadingAdvertisers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      (user.companyId && companies.find(c => c.id === user.companyId)?.name.toLowerCase().includes(userSearchTerm.toLowerCase()))
    );
  }, [users, companies, userSearchTerm]);

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    const term = companySearchTerm.toLowerCase();
    return companies.filter(company =>
      company.name.toLowerCase().includes(term) ||
      (!!company.advertiserId && !!companyAdvertisers[company.advertiserId]?.name.toLowerCase().includes(term)) ||
      (!!company.advertiserId && companySearchAdvertiserIds.has(company.advertiserId))
    );
  }, [companies, companyAdvertisers, companySearchTerm, companySearchAdvertiserIds]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = (userPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, userPage, itemsPerPage]);

  // Paginate companies
  const paginatedCompanies = useMemo(() => {
    const startIndex = (companyPage - 1) * itemsPerPage;
    return filteredCompanies.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCompanies, companyPage, itemsPerPage]);

  // Fetch advertiser details only for the companies visible on the current
  // page (not every advertiser-linked company in the database), skipping
  // IDs already in the cache. Re-runs whenever the visible page changes,
  // e.g. on page/page-size navigation or a search/filter change. A fetch
  // still merges into the cache even if the user has since navigated to a
  // different page before it resolves — the result is keyed by advertiser
  // ID and always valid, so discarding it would just force a redundant
  // refetch on returning to that page.
  useEffect(() => {
    const idsToFetch = Array.from(new Set(
      paginatedCompanies
        .filter(company => company.advertiserId && !companyAdvertisers[company.advertiserId as number])
        .map(company => company.advertiserId as number)
    ));

    if (idsToFetch.length === 0) return;

    (async () => {
      const results = await Promise.all(
        idsToFetch.map(async id => {
          try {
            const advertiser = await advertisersApi.getById(id);
            return [id, advertiser] as const;
          } catch (err) {
            console.error(`Failed to fetch advertiser ${id}:`, err);
            return null;
          }
        })
      );

      if (!isMountedRef.current) return;

      setCompanyAdvertisers(prev => {
        const next = { ...prev };
        for (const result of results) {
          if (result) next[result[0]] = result[1];
        }
        return next;
      });
    })();
  }, [paginatedCompanies]);

  // Create pagination info for users
  const userPaginationInfo: PaginationInfo = {
    page: userPage,
    limit: itemsPerPage,
    totalCount: filteredUsers.length,
    totalPages: Math.ceil(filteredUsers.length / itemsPerPage),
    hasNextPage: userPage < Math.ceil(filteredUsers.length / itemsPerPage),
    hasPreviousPage: userPage > 1,
    nextPage: userPage < Math.ceil(filteredUsers.length / itemsPerPage) ? userPage + 1 : null,
    previousPage: userPage > 1 ? userPage - 1 : null
  };

  // Create pagination info for companies
  const companyPaginationInfo: PaginationInfo = {
    page: companyPage,
    limit: itemsPerPage,
    totalCount: filteredCompanies.length,
    totalPages: Math.ceil(filteredCompanies.length / itemsPerPage),
    hasNextPage: companyPage < Math.ceil(filteredCompanies.length / itemsPerPage),
    hasPreviousPage: companyPage > 1,
    nextPage: companyPage < Math.ceil(filteredCompanies.length / itemsPerPage) ? companyPage + 1 : null,
    previousPage: companyPage > 1 ? companyPage - 1 : null
  };

  // Fill-or-hug rule (mirrors n3uyh in PromptManager): when the tab's TOTAL
  // filtered count reaches a full page, the table area fills the remaining
  // column height (flex-1) and scrolls internally; when sparse it hugs its
  // content height (max-h-full) so few rows leave no empty gap below.
  const isUsersFullPage = filteredUsers.length >= itemsPerPage;
  const isCompaniesFullPage = filteredCompanies.length >= itemsPerPage;

  // Reset to page 1 when search term changes
  useEffect(() => {
    setUserPage(1);
  }, [userSearchTerm]);

  useEffect(() => {
    setCompanyPage(1);
  }, [companySearchTerm]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchAdvertisers(advertiserSearchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [advertiserSearchTerm]);

  // Resolve advertiser IDs matching the company search term via the
  // advertisers search endpoint, so filteredCompanies can match by advertiser
  // name across the whole dataset instead of only cached/paginated ones.
  useEffect(() => {
    const term = companySearchTerm.trim();
    if (!term) {
      setCompanySearchAdvertiserIds(new Set());
      return;
    }

    let cancelled = false;

    const debounceTimer = setTimeout(async () => {
      try {
        const results = await advertisersApi.search(term);
        if (!cancelled) {
          setCompanySearchAdvertiserIds(new Set(results.map(advertiser => advertiser.id)));
        }
      } catch (err) {
        console.error('Failed to search advertisers for company filter:', err);
        if (!cancelled) {
          setCompanySearchAdvertiserIds(new Set());
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [companySearchTerm]);

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-ink mb-4">Access Denied</h1>
          <p className="text-ink-muted">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that non-admin users must have a company
    if ((formData.role === 'user' || formData.role === 'eskimi_admin' || formData.role === 'adops') && !formData.companyId) {
      setError('Non-admin users must be assigned to a company');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        companyId: formData.companyId || null
      };
      await userApi.createUser(dataToSend);
      setShowCreateModal(false);
      setFormData({ email: '', name: '', role: 'eskimi_admin', provider: 'local', companyId: '' });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    // Validate that non-admin users must have a company
    if ((formData.role === 'user' || formData.role === 'eskimi_admin' || formData.role === 'adops') && !formData.companyId) {
      setError('Non-admin users must be assigned to a company');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        companyId: formData.companyId || null
      };
      await userApi.updateUser(editingUser.id, dataToSend);
      setEditingUser(null);
      setFormData({ email: '', name: '', role: 'eskimi_admin', provider: 'local', companyId: '' });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await userApi.deleteUser(userId);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      provider: user.provider,
      companyId: user.companyId || ''
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
    setFormData({ email: '', name: '', role: 'eskimi_admin', provider: 'local', companyId: '' });
  };

  // Company management functions
  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await companyApi.create(companyFormData);
      setShowCreateCompanyModal(false);
      setCompanyFormData({ name: '', advertiserId: null });
      fetchCompanies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompany) return;

    try {
      await companyApi.update(editingCompany.id, companyFormData);
      setEditingCompany(null);
      setCompanyFormData({ name: '', advertiserId: null });
      fetchCompanies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update company');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;

    try {
      await companyApi.delete(companyId);
      fetchCompanies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete company');
    }
  };

  const openEditCompanyModal = async (company: Company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name,
      advertiserId: company.advertiserId || null
    });

    // If company has an advertiser, fetch its name
    if (company.advertiserId) {
      try {
        const advertiser = await advertisersApi.getById(company.advertiserId);
        setAdvertiserSearchTerm(advertiser.name);
      } catch (err) {
        console.error('Failed to fetch advertiser:', err);
        setAdvertiserSearchTerm('');
      }
    } else {
      setAdvertiserSearchTerm('');
    }
  };

  const closeCompanyModal = () => {
    setShowCreateCompanyModal(false);
    setEditingCompany(null);
    setCompanyFormData({ name: '', advertiserId: null });
    setAdvertiserSearchTerm('');
  };

  const handleGenerateRegistrationLink = async (userId: string) => {
    setGeneratingLink(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem('accessToken');
      // Remove trailing slash from API_BASE_URL if it exists to prevent double slashes
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const response = await axios.post(
        `${baseUrl}/api/auth/generate-registration-link`,
        { userId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        setRegistrationLink(response.data.registrationUrl);
        setLinkCopied(false);
        setShowRegistrationLinkModal(true);
      }
    } catch (err: any) {
      console.error('Generate registration link error:', err);
      setError(err.response?.data?.message || 'Failed to generate registration link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy link. Please copy it manually.');
    }
  };

  const closeRegistrationLinkModal = () => {
    setShowRegistrationLinkModal(false);
    setRegistrationLink('');
    setLinkCopied(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-active"></div>
      </div>
    );
  }

  const isNonAdminRole = formData.role === 'user' || formData.role === 'eskimi_admin' || formData.role === 'adops';

  return (
    <div className="flex flex-col space-y-4 h-full min-h-0">
        {/* Search + Users/Companies toggle + Add button in one row, search
            expanded — mirrors SearchFilterBar.tsx's toolbar arrangement
            (deepcontext-fbj1.2). -mt-[21px] pulls the row's centerline up to
            match the sidebar's Explore pill centerline (deepcontext-jlkv
            mechanism, deepcontext-mma5 — mirrors SearchFilterBar.tsx:39). */}
        <div className="-mt-[21px] flex flex-wrap items-center gap-3" data-testid="users-toolbar-row">
          <SearchInput
            placeholder={activeTab === 'users' ? 'Search users by name, email, or company...' : 'Search companies by name or advertiser...'}
            value={activeTab === 'users' ? userSearchTerm : companySearchTerm}
            onChange={(e) => activeTab === 'users' ? setUserSearchTerm(e.target.value) : setCompanySearchTerm(e.target.value)}
            className="flex-1"
            data-testid={activeTab === 'users' ? 'users-search-input' : 'companies-search-input'}
          />
          <div className="ml-auto flex flex-shrink-0 items-center gap-3">
            <Tabs
              tabs={[
                { value: 'users', label: `Users · ${users.length}` },
                { value: 'companies', label: `Companies · ${companies.length}` },
              ]}
              value={activeTab}
              onChange={(value) => setActiveTab(value as 'users' | 'companies')}
            />
            <Button
              onClick={() => activeTab === 'users' ? setShowCreateModal(true) : setShowCreateCompanyModal(true)}
              data-testid="users-page-add-button"
            >
              {activeTab === 'users' ? 'Create User' : 'Create Company'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-risk-bg p-4">
            <div className="text-sm text-risk">{error}</div>
          </div>
        )}

        {/* Users Table */}
        {activeTab === 'users' && (
          <div className={`${fillOrHugClass(isUsersFullPage)} min-h-0 flex flex-col gap-4`}>
            <div
              className={`${fillOrHugClass(isUsersFullPage)} flex min-h-0 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm`}
              data-testid="users-table-fill-box"
            >
              <TableFillBox isFullPage={isUsersFullPage}>
                <Table className={tableFillHeightClass(isUsersFullPage)}>
                  <TableHead>
                    <TableRow className="hover:bg-transparent">
                      <TableHeaderCell>User</TableHeaderCell>
                      <TableHeaderCell>Role</TableHeaderCell>
                      <TableHeaderCell>Company</TableHeaderCell>
                      <TableHeaderCell>Provider</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-accent text-xs font-bold text-white">
                              {getInitials(user.name)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-bold text-ink">{user.name}</div>
                              <div className="truncate text-xs text-ink-muted">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariant(user.role)}>{formatRoleDisplay(user.role)}</Badge>
                        </TableCell>
                        <TableCell>
                          {user.companyId ? (
                            <Badge variant="default">
                              {companies.find(c => c.id === user.companyId)?.name || 'Unknown'}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">No Company</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-ink-muted">{user.provider}</TableCell>
                        <TableCell className="text-ink-muted">{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {user.provider === 'local' && (
                              <ActionTooltip label="Generate password link">
                                <ActionIconButton
                                  kind="ghost-safe"
                                  onClick={() => handleGenerateRegistrationLink(user.id)}
                                  disabled={generatingLink}
                                  aria-label="Generate password link"
                                  data-testid={`user-password-link-${user.id}`}
                                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <KeyIcon className="h-4 w-4" />
                                </ActionIconButton>
                              </ActionTooltip>
                            )}
                            <ActionTooltip label="Edit">
                              <ActionIconButton
                                kind="ghost"
                                onClick={() => openEditModal(user)}
                                aria-label="Edit"
                                data-testid={`user-edit-${user.id}`}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </ActionIconButton>
                            </ActionTooltip>
                            {user.id !== currentUser?.id && (
                              <ActionTooltip align="end" label="Delete">
                                <ActionIconButton
                                  kind="ghost-danger"
                                  onClick={() => handleDeleteUser(user.id)}
                                  aria-label="Delete"
                                  data-testid={`user-delete-${user.id}`}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </ActionIconButton>
                              </ActionTooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableFillBox>
              {/* Pagination footer INSIDE the card (locked table-card spec,
                  deepcontext-c0p7.9) — mirrors Explore.tsx's renderPagination(). */}
              {userPaginationInfo.totalPages > 1 && (
                <div data-testid="users-pagination" className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
                  <Pagination
                    pagination={userPaginationInfo}
                    onPageChange={setUserPage}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Companies Table */}
        {activeTab === 'companies' && (
          <div className={`${fillOrHugClass(isCompaniesFullPage)} min-h-0 flex flex-col gap-4`}>
            <div
              className={`${fillOrHugClass(isCompaniesFullPage)} flex min-h-0 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm`}
              data-testid="companies-table-fill-box"
            >
              <TableFillBox isFullPage={isCompaniesFullPage}>
                <Table className={tableFillHeightClass(isCompaniesFullPage)}>
                  <TableHead>
                    <TableRow className="hover:bg-transparent">
                      <TableHeaderCell>Company Name</TableHeaderCell>
                      <TableHeaderCell>Advertiser</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Users</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCompanies.map((company) => (
                      <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                        <TableCell className="font-bold text-ink">{company.name}</TableCell>
                        <TableCell className="text-ink-muted">
                          {company.advertiserId && companyAdvertisers[company.advertiserId]
                            ? `${companyAdvertisers[company.advertiserId].name} (${company.advertiserId})`
                            : ''}
                        </TableCell>
                        <TableCell>
                          <Badge variant={company.isActive ? 'safe' : 'risk'}>
                            {company.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-ink-muted">
                          {users.filter(u => u.companyId === company.id).length}
                        </TableCell>
                        <TableCell className="text-ink-muted">{new Date(company.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <ActionTooltip label="Edit">
                              <ActionIconButton
                                kind="ghost"
                                onClick={() => openEditCompanyModal(company)}
                                aria-label="Edit"
                                data-testid={`company-edit-${company.id}`}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </ActionIconButton>
                            </ActionTooltip>
                            <ActionTooltip align="end" label="Delete">
                              <ActionIconButton
                                kind="ghost-danger"
                                onClick={() => handleDeleteCompany(company.id)}
                                aria-label="Delete"
                                data-testid={`company-delete-${company.id}`}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </ActionIconButton>
                            </ActionTooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableFillBox>
              {/* Pagination footer INSIDE the card (locked table-card spec,
                  deepcontext-c0p7.9) — mirrors Explore.tsx's renderPagination(). */}
              {companyPaginationInfo.totalPages > 1 && (
                <div data-testid="companies-pagination" className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
                  <Pagination
                    pagination={companyPaginationInfo}
                    onPageChange={setCompanyPage}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create/Edit User Modal */}
        <Modal
          open={showCreateModal || !!editingUser}
          onClose={closeModal}
          title={editingUser ? 'Edit User' : 'Create New User'}
          className="max-w-lg"
        >
          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">Email</label>
              <input
                type="email"
                required
                data-testid="user-form-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={PILL_INPUT}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">Name</label>
              <input
                type="text"
                required
                data-testid="user-form-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={PILL_INPUT}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">Role</label>
              <select
                data-testid="user-form-role-select"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'eskimi_admin' | 'adops' | 'admin' })}
                className={PILL_INPUT}
              >
                <option value="user">User</option>
                <option value="eskimi_admin">Eskimi User</option>
                <option value="adops">AdOps</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">
                Company {isNonAdminRole && <span className="text-risk">*</span>}
              </label>
              <select
                data-testid="user-form-company-select"
                value={formData.companyId || ''}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                className={PILL_INPUT}
                required={isNonAdminRole}
              >
                <option value="">{formData.role === 'admin' ? 'No Company' : 'Select a company...'}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {isNonAdminRole && (
                <p className="mt-1.5 text-xs text-ink-muted">
                  Non-admin users must be assigned to a company
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={closeModal}
                data-testid="user-form-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="user-form-submit-button"
              >
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Create/Edit Company Modal */}
        <Modal
          open={showCreateCompanyModal || !!editingCompany}
          onClose={closeCompanyModal}
          title={editingCompany ? 'Edit Company' : 'Create New Company'}
          className="max-w-lg"
        >
          <form onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">Company Name</label>
              <input
                type="text"
                required
                data-testid="company-form-name-input"
                value={companyFormData.name}
                onChange={(e) => setCompanyFormData({ ...companyFormData, name: e.target.value })}
                className={PILL_INPUT}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">Eskimi Advertiser</label>
              <input
                type="text"
                placeholder="Search for advertiser..."
                data-testid="company-form-advertiser-search-input"
                value={advertiserSearchTerm}
                onChange={(e) => setAdvertiserSearchTerm(e.target.value)}
                className={PILL_INPUT}
              />
              {loadingAdvertisers && (
                <p className="mt-1.5 text-xs text-ink-muted">Searching...</p>
              )}
              {advertisers.length > 0 && (
                <div className="mt-1.5 max-h-40 overflow-y-auto rounded-xl border border-line">
                  {advertisers.map((advertiser) => (
                    <div
                      key={advertiser.id}
                      onClick={() => {
                        setCompanyFormData({ ...companyFormData, advertiserId: advertiser.id });
                        setAdvertiserSearchTerm(advertiser.name);
                        setAdvertisers([]);
                      }}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-ghost-bg ${
                        companyFormData.advertiserId === advertiser.id ? 'bg-nav-active text-accent-active' : 'text-ink'
                      }`}
                    >
                      {advertiser.name}
                    </div>
                  ))}
                </div>
              )}
              {companyFormData.advertiserId && (
                <button
                  type="button"
                  onClick={() => {
                    setCompanyFormData({ ...companyFormData, advertiserId: null });
                    setAdvertiserSearchTerm('');
                  }}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-risk hover:opacity-80"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Clear selection
                </button>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={closeCompanyModal}
                data-testid="company-form-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="company-form-submit-button"
              >
                {editingCompany ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Registration Link Modal */}
        <Modal
          open={showRegistrationLinkModal}
          onClose={closeRegistrationLinkModal}
          title="Password Setup Link Generated"
          className="max-w-lg"
        >
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-safe bg-safe-bg p-4">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-safe" />
            <div className="text-sm text-safe">
              <p className="font-semibold">Success!</p>
              <p className="mt-1">
                Share this one-time link with the user to let them set up or reset their password.
                It expires in 24 hours and can only be used once.
              </p>
            </div>
          </div>

          <label className="mb-1.5 block text-sm font-semibold text-ink">Password setup URL</label>
          <div className="flex items-center gap-2">
            <div
              className="flex-1 truncate rounded-full border border-line bg-ghost-bg px-4 py-2 font-mono text-xs text-ink"
              data-testid="registration-link-value"
            >
              {registrationLink}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={copyToClipboard}
              data-testid="registration-link-copy-button"
            >
              {linkCopied ? 'Copied!' : 'Copy'}
            </Button>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={closeRegistrationLinkModal}>Close</Button>
          </div>
        </Modal>
    </div>
  );
};

export default UserManagement;
